<script setup lang="ts" generic="T extends Key = Key">
import { vOnClickOutside } from '@vueuse/components'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Key, Options } from '~/static/types/base'

interface Props {
  options: Options<T>
  selected: T | null
  nullText?: string | null
  tabindex?: number
  hasError?: boolean
  placeholder?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  selected: null,
  nullText: null,
  tabindex: 0,
  hasError: false,
  placeholder: null,
})

const emit = defineEmits<{
  (e: 'change', val: T | null): void
}>()

const textByValue = computed(() => {
  type textByValueType = { [value: string]: string }
  const textByValue: textByValueType = {}
  props.options.forEach((option) => {
    textByValue[option.value] = option.text
  })
  return textByValue
})

const selectedText = computed(() => {
  if (props.selected) {
    return textByValue.value[props.selected]
  }
  if (props.nullText) {
    return props.nullText
  }
  return ''
})

const open = ref(false)
const handleChange = (value: T | null) => {
  open.value = false
  emit('change', value)
}

</script>

<template>
  <div
    v-on-click-outside="() => { open = false }"
    class="select-group"
    :class="{ 'select-group--error': hasError }"
    :tabindex="tabindex"
  >
    <div
      class="form-select selected"
      :class="{ 'open': open }"
      @click="open = !open"
    >
      {{ selectedText }}
      <div
        v-if="placeholder && selected == null"
        class="placeholder-select"
      >
        {{ placeholder }}
      </div>
    </div>
    <div
      class="items"
      :class="{ 'select_hide': !open }"
    >
      <div
        v-for="option of options"
        :key="<string>option.value"
        class="select-item"
        @click="handleChange(option.value)"
      >
        {{ option.text }}
      </div>
      <div
        class="select-item custom-link"
        @click="handleChange(null)"
      >
        +&nbsp;Создать папку
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.select-group {
  position: relative;
  width: 100%;

  .selected {
    cursor: pointer;
    height: rem(38px);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    &.open {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    .placeholder-select {
      color: $gray-600;
    }
  }

  .selected__btn {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(38px);
    height: rem(38px);

    .icon {
      transition: .3s ease;
    }
  }

  .items {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    z-index: 10;
    max-height: rem(240px);
    border-radius: rem(4px);
    background: $white;
    border: 1px solid $gray-300;
    overflow-y: auto;

    .select-item {
      cursor: pointer;
      padding: rem(4px) rem(12px);

      &:hover {
        background: $indigo-100-light;
      }
    }
  }
}

.select_hide {
  display: none;
}
</style>
